/**
 * MyAlertsPage - View and manage price alerts
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Bell, Trash2, Calendar, MapPin, Loader2, ArrowLeft,
  AlertCircle, BellOff, BellRing, Target, TrendingDown,
  Home, CheckCircle2, ChevronDown, ChevronUp, LineChart
} from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../context/CountryContext';
import { ComparisonPriceHistory } from '../components/PriceHistoryChart';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyAlertsPage() {
  const navigate = useNavigate();
  const { formatCurrency } = useCountry();
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [expandedAlertId, setExpandedAlertId] = useState(null);
  
  // Load email from localStorage
  useEffect(() => {
    const savedEmail = localStorage.getItem('savedComparisonsEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setEmailSubmitted(true);
    }
  }, []);
  
  // Fetch alerts when email is submitted
  useEffect(() => {
    if (emailSubmitted && email) {
      fetchAlerts();
    }
  }, [emailSubmitted, email]);
  
  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/compare/price-alerts?user_email=${encodeURIComponent(email)}`);
      if (response.ok) {
        const data = await response.json();
        setAlerts(data.alerts || []);
      } else {
        toast.error('Failed to fetch price alerts');
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    localStorage.setItem('savedComparisonsEmail', email);
    setEmailSubmitted(true);
  };
  
  const handleDelete = async (alertId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/compare/price-alert/${alertId}?user_email=${encodeURIComponent(email)}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        setAlerts(prev => prev.filter(a => a.id !== alertId));
        toast.success('Price alert deleted');
      } else {
        toast.error('Failed to delete alert');
      }
    } catch (error) {
      console.error('Error deleting alert:', error);
      toast.error('Failed to delete alert');
    }
    setDeleteConfirm(null);
  };
  
  const handleToggle = async (alertId) => {
    setTogglingId(alertId);
    try {
      const response = await fetch(
        `${API_URL}/api/compare/price-alert/${alertId}/toggle?user_email=${encodeURIComponent(email)}`,
        { method: 'PUT' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAlerts(prev => prev.map(a => 
          a.id === alertId ? { ...a, is_active: data.is_active } : a
        ));
        toast.success(data.message);
      } else {
        toast.error('Failed to update alert');
      }
    } catch (error) {
      console.error('Error toggling alert:', error);
      toast.error('Failed to update alert');
    } finally {
      setTogglingId(null);
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const getAlertTypeLabel = (type) => {
    switch (type) {
      case 'any': return 'Any property drops';
      case 'all': return 'All properties drop';
      case 'average': return 'Average price drops';
      default: return type;
    }
  };
  
  const changeEmail = () => {
    localStorage.removeItem('savedComparisonsEmail');
    setEmail('');
    setEmailSubmitted(false);
    setAlerts([]);
  };
  
  // Email entry form
  if (!emailSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 py-8" data-testid="my-alerts-page">
        <div className="container mx-auto max-w-md px-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <Bell className="h-6 w-6 text-orange-600" />
              </div>
              <CardTitle>My Price Alerts</CardTitle>
              <p className="text-sm text-gray-500 mt-2">
                Enter your email to view and manage your price alerts
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                />
                <Button 
                  type="submit" 
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  data-testid="view-alerts-btn"
                >
                  View My Alerts
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8" data-testid="my-alerts-page">
      <div className="container mx-auto max-w-4xl px-3 sm:px-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="p-1 sm:p-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Bell className="h-5 w-5 sm:h-6 sm:w-6 text-orange-500" />
                My Price Alerts
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate max-w-[200px] sm:max-w-none">
                {email} · <button onClick={changeEmail} className="text-orange-600 hover:underline">Change</button>
              </p>
            </div>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAlerts}
            disabled={loading}
            className="self-end sm:self-auto"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
          </Button>
        </div>
        
        {/* Push Notification Settings */}
        <PushNotificationSettings userEmail={email} />
        
        {/* Loading state */}
        {loading && alerts.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        )}
        
        {/* Empty state */}
        {!loading && alerts.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <BellOff className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No price alerts</h3>
              <p className="text-gray-500 mb-4">
                Set up alerts to get notified when property prices drop
              </p>
              <Button 
                onClick={() => navigate('/category/property')}
                className="bg-orange-500 hover:bg-orange-600"
              >
                Browse Properties
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Alerts list */}
        <div className="space-y-3 sm:space-y-4">
          {alerts.map((alert) => (
            <Card 
              key={alert.id} 
              className={`overflow-hidden transition-all ${!alert.is_active ? 'opacity-60' : ''}`} 
              data-testid={`alert-${alert.id}`}
            >
              <div className={`p-3 sm:p-4 border-b ${alert.triggered ? 'bg-green-50' : 'bg-gradient-to-r from-orange-50 to-amber-50'}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
                  <div className="flex items-center gap-2 sm:gap-3">
                    {alert.triggered ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                    ) : alert.is_active ? (
                      <BellRing className="h-5 w-5 text-orange-500 flex-shrink-0" />
                    ) : (
                      <BellOff className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                        {alert.title}
                      </h3>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <Calendar className="h-3 w-3" />
                        Created {formatDate(alert.created_at)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 self-end sm:self-auto">
                    {alert.triggered ? (
                      <Badge className="bg-green-100 text-green-700">Triggered</Badge>
                    ) : (
                      <Badge variant={alert.is_active ? "default" : "secondary"} className={alert.is_active ? "bg-orange-500" : ""}>
                        {alert.is_active ? 'Active' : 'Paused'}
                      </Badge>
                    )}
                    
                    <div className="flex items-center gap-1">
                      <Switch
                        checked={alert.is_active}
                        onCheckedChange={() => handleToggle(alert.id)}
                        disabled={togglingId === alert.id || alert.triggered}
                        data-testid={`toggle-${alert.id}`}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteConfirm(alert.id)}
                        data-testid={`delete-${alert.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Alert details */}
              <CardContent className="p-3 sm:p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Target price info */}
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Target className="h-5 w-5 text-orange-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Target Price</p>
                      <p className="text-lg font-bold text-orange-600">
                        {formatCurrency(alert.target_price)}/mo
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        <TrendingDown className="h-3 w-3 inline mr-1" />
                        {getAlertTypeLabel(alert.alert_type)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Properties being tracked */}
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Home className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Tracking {alert.listings?.length || 0} properties
                      </p>
                      <div className="space-y-1">
                        {alert.listings?.slice(0, 3).map((listing, idx) => (
                          <div key={idx} className="flex justify-between text-xs">
                            <span className="text-gray-600 truncate max-w-[120px]">{listing.title}</span>
                            <span className="font-medium text-blue-600">{formatCurrency(listing.price)}</span>
                          </div>
                        ))}
                        {(alert.listings?.length || 0) > 3 && (
                          <p className="text-xs text-gray-400">+{alert.listings.length - 3} more</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Expand/collapse price history button */}
                <div className="mt-3 flex justify-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedAlertId(expandedAlertId === alert.id ? null : alert.id)}
                    className="text-blue-600 hover:text-blue-700"
                    data-testid={`expand-history-${alert.id}`}
                  >
                    <LineChart className="h-4 w-4 mr-1" />
                    {expandedAlertId === alert.id ? 'Hide' : 'View'} Price History
                    {expandedAlertId === alert.id ? (
                      <ChevronUp className="h-4 w-4 ml-1" />
                    ) : (
                      <ChevronDown className="h-4 w-4 ml-1" />
                    )}
                  </Button>
                </div>
                
                {/* Expandable Price History Chart */}
                {expandedAlertId === alert.id && alert.listing_ids?.length > 0 && (
                  <div className="mt-4 border-t pt-4">
                    <ComparisonPriceHistory 
                      listingIds={alert.listing_ids}
                      titles={alert.listings?.map(l => l.title) || []}
                    />
                  </div>
                )}
                
                {/* Last checked */}
                {alert.last_checked && (
                  <p className="text-xs text-gray-400 mt-3 text-center">
                    Last checked: {formatDate(alert.last_checked)}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
        
        {/* Delete confirmation dialog */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                Delete Price Alert
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this price alert? You will no longer receive notifications for these properties.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleDelete(deleteConfirm)}
              >
                Delete Alert
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

// Push Notification Settings Component
function PushNotificationSettings({ userEmail }) {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [permission, setPermission] = useState('default');
  
  useEffect(() => {
    // Check if push notifications are supported
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      checkSubscription();
    }
  }, []);
  
  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (err) {
      console.error('Error checking subscription:', err);
    }
  };
  
  const handleToggle = async () => {
    if (!isSupported) return;
    
    setIsLoading(true);
    
    try {
      if (isSubscribed) {
        // Unsubscribe
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
          // Remove from server
          await fetch(`${API_URL}/api/push/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`, {
            method: 'DELETE'
          });
        }
        setIsSubscribed(false);
        toast.success('Push notifications disabled');
      } else {
        // Subscribe
        if (Notification.permission === 'default') {
          const result = await Notification.requestPermission();
          setPermission(result);
          if (result !== 'granted') {
            toast.error('Please allow notifications to receive price alerts');
            setIsLoading(false);
            return;
          }
        } else if (Notification.permission === 'denied') {
          toast.error('Notifications are blocked. Please enable them in browser settings.');
          setIsLoading(false);
          return;
        }
        
        // Register service worker and subscribe
        const registration = await navigator.serviceWorker.register('/push-sw.js');
        await navigator.serviceWorker.ready;
        
        // For demo, we'll just mark as subscribed without actual VAPID key subscription
        // In production, you'd use the VAPID public key from the server
        
        // Store subscription status on server
        await fetch(`${API_URL}/api/push/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            subscription: {
              endpoint: 'browser-notification-' + Date.now(),
              keys: { p256dh: 'demo-key', auth: 'demo-auth' }
            },
            user_email: userEmail
          })
        });
        
        setIsSubscribed(true);
        toast.success('Push notifications enabled! You\'ll be notified when prices drop.');
      }
    } catch (err) {
      console.error('Error toggling notifications:', err);
      toast.error('Failed to update notification settings');
    } finally {
      setIsLoading(false);
    }
  };
  
  if (!isSupported) {
    return null; // Don't show if not supported
  }
  
  return (
    <Card className="mb-4 bg-gradient-to-r from-orange-50 to-amber-50 border-orange-100" data-testid="push-notification-settings">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isSubscribed ? (
              <BellRing className="h-5 w-5 text-orange-500" />
            ) : (
              <BellOff className="h-5 w-5 text-gray-400" />
            )}
            <div>
              <p className="font-medium text-gray-900">Push Notifications</p>
              <p className="text-xs text-gray-500">
                {isSubscribed 
                  ? 'Get instant alerts when prices drop'
                  : 'Enable to receive instant price drop alerts'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {permission === 'denied' && (
              <Badge variant="outline" className="text-red-600 border-red-200">
                Blocked
              </Badge>
            )}
            <Switch
              checked={isSubscribed}
              onCheckedChange={handleToggle}
              disabled={isLoading || permission === 'denied'}
              data-testid="push-notification-toggle"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
